import type { ScoreBreakdown, SequenceSurfacing } from '../types/lifecycle';
import { formatDateTime } from '../utils/lifecycleFormat';

// PR-S4b reader/UI surfacing for the sourced sequence context. These components
// render ONLY what the audit-backed payload already carries. They must stay
// cautious: the badge names a surfaced state, it is not a proof of intent and it
// never reflects the risk score. When `surfacing` is absent/null nothing renders.

function postureLabel(posture: string | undefined): string {
  const raw = String(posture || '').toUpperCase();
  if (raw === 'ELEVATED') return 'zvýšená pozornost';
  if (raw === 'NORMAL') return 'běžný';
  return raw ? raw.toLocaleLowerCase('cs-CZ') : 'neuvedeno';
}

function badgeText(surfacing: SequenceSurfacing): string {
  // Prefer the cautious server-side label ("sekvenční kontext" / "…konvergence");
  // fall back to a neutral posture phrasing so we never fabricate a stronger claim.
  return surfacing.label || `sekvenční kontext (${postureLabel(surfacing.posture)})`;
}

export function SequenceBadge({ surfacing }: { surfacing: SequenceSurfacing | null | undefined }) {
  if (!surfacing) return null;
  return (
    <span
      className="badge badge--sequence"
      title="Sekvenční kontext z dřívějších záznamů. Pomocný signál pro sledování, neovlivňuje rizikové skóre."
    >
      {badgeText(surfacing)}
    </span>
  );
}

export function SequencePanel({ surfacing }: { surfacing: SequenceSurfacing | null | undefined }) {
  if (!surfacing) return null;
  const stepCount = surfacing.step_ids?.length ?? 0;
  const attemptCount = surfacing.attempt_ids?.length ?? 0;

  return (
    <section className="detail-panel__section detail-panel__section--sequence" aria-label="Sekvenční kontext">
      <h4>
        <span aria-hidden="true">◇</span> Sekvenční kontext
      </h4>
      <p className="sequence-panel__note">
        Pomocný signál z dřívějších sledovaných záznamů. Neslouží jako důkaz záměru a neovlivňuje rizikové skóre.
      </p>
      <dl className="sequence-panel__facts">
        <dt>Stav</dt>
        <dd>{badgeText(surfacing)}</dd>
        <dt>Posture</dt>
        <dd>{postureLabel(surfacing.posture)}</dd>
        {surfacing.computed_at && (
          <>
            <dt>Vypočteno</dt>
            <dd>{formatDateTime(surfacing.computed_at)}</dd>
          </>
        )}
        <dt>Záznamy</dt>
        <dd>
          {stepCount} {stepCount === 1 ? 'krok' : stepCount >= 2 && stepCount <= 4 ? 'kroky' : 'kroků'}
          {' · '}
          {attemptCount} {attemptCount === 1 ? 'pokus' : attemptCount >= 2 && attemptCount <= 4 ? 'pokusy' : 'pokusů'}
        </dd>
      </dl>
    </section>
  );
}

// PR-S3a breakdown-only audit/debug panel. Renders the sourced context_pressure
// rozklad EXACTLY as published — it is read-only audit data and NEVER reflects or
// changes the risk score. Stays cautious: a neutral note repeats that this does not
// affect the displayed risk, and the components are shown as plain key/value rows.
function formatBreakdownValue(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return String(value);
}

export function ScoreBreakdownPanel({ breakdown }: { breakdown: ScoreBreakdown | null | undefined }) {
  if (!breakdown) return null;
  const components = breakdown.components ?? [];

  return (
    <section
      className="detail-panel__section detail-panel__section--score-breakdown"
      aria-label="Rozklad sekvenčního tlaku (audit/debug)"
    >
      <h4>
        <span aria-hidden="true">◇</span> Rozklad sekvenčního tlaku (audit/debug)
      </h4>
      <p className="sequence-panel__note">
        Pouze auditovatelný rozklad pro čtení a ladění. Neovlivňuje rizikové skóre, výstrahu ani
        rizikovou úroveň a není to aktivace skórování.
      </p>
      <dl className="sequence-panel__facts">
        <dt>Sekvenční tlak (context_pressure)</dt>
        <dd>
          {formatBreakdownValue(breakdown.context_pressure)}
          {breakdown.active === false && ' (neaktivní)'}
        </dd>
        {components.length > 0 && components.map((c) => (
          <span key={c.key} style={{ display: 'contents' }}>
            <dt>{c.label}</dt>
            <dd>{formatBreakdownValue(c.value)}</dd>
          </span>
        ))}
        {breakdown.metadata?.published_clamp != null && (
          <>
            <dt>Publikační strop (T3)</dt>
            <dd>{breakdown.metadata.published_clamp} (jen marker, v S3a se nic neclampuje)</dd>
          </>
        )}
      </dl>
    </section>
  );
}
